/**
 * support-link-picker Stimulus controller — "Link a page" in the staff support
 * composer (templates/components/support_thread.html, staff mode only).
 *
 * The button opens a search over the pages the server offers
 * (data-support-link-picker-pages-value, from support_link_pages() in
 * application/utils/support_links.py). Choosing one writes [label](/path)
 * into the message box at the cursor, the selected text becoming the label;
 * renderBody in _support_messages.js turns it into a link once sent. Staff
 * never need to know a page's address.
 *
 * Escape closes the search without letting ai-chat's document Escape handler
 * close the whole widget. Pure logic: _support_link_picker.js.
 */
import { Controller } from "@hotwired/stimulus";
import { insertLink, matchPages } from "./_support_link_picker.js";
import { escapeHtml } from "./_support_messages.js";

export default class extends Controller {
    static targets = ['input', 'panel', 'query', 'results'];
    static values = { pages: Array };

    toggle() {
        if (this.panelTarget.hidden) this.open();
        else this.close();
    }

    open() {
        this._selection = [this.inputTarget.selectionStart, this.inputTarget.selectionEnd];
        this.panelTarget.hidden = false;
        this.queryTarget.value = '';
        this.filter();
        this.queryTarget.focus();
    }

    close() {
        this.panelTarget.hidden = true;
    }

    filter() {
        this._matches = matchPages(this.pagesValue, this.queryTarget.value);
        this.resultsTarget.innerHTML = this._matches.length
            ? this._matches.map((page, index) => this._resultHtml(page, index)).join('')
            : '<p class="support-link-empty">No pages match</p>';
    }

    keydown(event) {
        if (event.key === 'Escape') {
            event.stopPropagation();
            this.close();
            this.inputTarget.focus();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (this._matches.length) this._insert(this._matches[0]);
        }
    }

    choose(event) {
        this._insert(this._matches[Number(event.currentTarget.dataset.index)]);
    }

    _insert(page) {
        const [start, end] = this._selection;
        const { text, caret } = insertLink(this.inputTarget.value, start, end, page);
        this.inputTarget.value = text;
        this.close();
        this.inputTarget.focus();
        this.inputTarget.setSelectionRange(caret, caret);
        // support-chat sizes the box and signals typing on input.
        this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    }

    _resultHtml(page, index) {
        return `<button type="button" class="support-link-result" role="option" data-index="${index}"
                        data-action="support-link-picker#choose">
                    <span>${escapeHtml(page.label)}</span>
                    <small>${escapeHtml(page.path)}</small>
                </button>`;
    }
}
